import { useEffect, useMemo, useState } from 'react';

import { toast } from 'sonner';

import {
  emptyEventFormValues,
  eventToFormValues,
  ProceduralEventFields,
  useProceduralEventForm,
} from './procedural-event-form';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { CreateProceduralEventInput, useCreateProceduralEvent } from '@/api/mutations/useCreateProceduralEvent';
import { UpdateProceduralEventInput, useUpdateProceduralEvent } from '@/api/mutations/useUpdateProceduralEvent';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Switch } from '@/new/components/shadcn/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { useTimelineCalculatorStore } from '@/new/stores/timeline-calculator-store';

interface EventDialogProps {
  /** `null` opens the dialog in create mode. */
  event: ProceduralTimelineEvent | null;
  eventsById: Record<string, ProceduralTimelineEvent>;
  matterId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EventDialog({ event, eventsById, matterId, open, onOpenChange }: EventDialogProps) {
  const isEdit = event !== null;
  const createMutation = useCreateProceduralEvent();
  const updateMutation = useUpdateProceduralEvent();
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  const initialValues = useMemo(() => (event !== null ? eventToFormValues(event) : emptyEventFormValues()), [event]);
  const form = useProceduralEventForm({
    open,
    initialValues,
    resetKey: event?.id ?? 'new',
    eventsById,
    currentEventId: event?.id ?? null,
  });

  const [recalculateDependents, setRecalculateDependents] = useState(true);
  const [isUpdateConfirmOpen, setIsUpdateConfirmOpen] = useState(false);
  const [pendingShared, setPendingShared] = useState<CreateProceduralEventInput | null>(null);
  const closeFloatingCalculator = useTimelineCalculatorStore((s) => s.close);

  useEffect(() => {
    if (!open) return;
    setIsUpdateConfirmOpen(false);
    setRecalculateDependents(true);
    closeFloatingCalculator();
  }, [open, event, closeFloatingCalculator]);

  // How many other events have their date calculated from the one being edited.
  const dependentCount = useMemo(
    () => (event === null ? 0 : Object.values(eventsById).filter((e) => e.anchorEventId === event.id).length),
    [eventsById, event],
  );
  const hasDependents = dependentCount > 0;

  // A cascade only happens when the start date moves (the sole recalculation
  // trigger), so surface the dependent warning + recalculation toggle — and
  // confirm on save — only once the start date has actually changed.
  const willCascade = isEdit && hasDependents && form.startDateChanged;

  const runCreate = (shared: CreateProceduralEventInput) => {
    createMutation.mutate(
      { matterId, input: shared },
      {
        onSuccess: () => {
          toast.success('Event created.');
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to create event.');
        },
      },
    );
  };

  const runUpdate = (shared: CreateProceduralEventInput) => {
    const input: UpdateProceduralEventInput = { ...shared, recalculateDependents };
    updateMutation.mutate(
      { matterId, eventId: event!.id, input },
      {
        onSuccess: () => {
          toast.success('Event updated.');
          setIsUpdateConfirmOpen(false);
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to update event.');
        },
      },
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const shared = form.buildInput({ requireStartDate: !isEdit, requireTimeZone: !isEdit });
    if (shared === null) return;
    if (!isEdit) {
      runCreate(shared);
      return;
    }
    // Confirm before saving only when the edit can cascade: the event has
    // dependents and its start date moved. Otherwise there's nothing to
    // propagate, so save directly even while suggestions are under review.
    if (willCascade) {
      setPendingShared(shared);
      setIsUpdateConfirmOpen(true);
      return;
    }
    runUpdate(shared);
  };

  const handleConfirmUpdate = () => {
    if (pendingShared !== null) runUpdate(pendingShared);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit event' : 'Create event'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the procedural event details.' : 'Add a new event to the timeline.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          {willCascade && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
              <p>
                Other procedural events are dependent on this event. Ensure subsequent events are reviewed upon update.
              </p>
            </div>
          )}

          <ProceduralEventFields form={form} matterId={matterId} isEdit={isEdit} eventsById={eventsById} />

          {willCascade && (
            <div className="flex items-center gap-4">
              <Switch
                id="recalc-dependents"
                checked={recalculateDependents}
                onCheckedChange={setRecalculateDependents}
              />
              <div className="space-y-1">
                <label htmlFor="recalc-dependents" className="block cursor-pointer text-sm font-medium text-gray-800">
                  Suggest updates to dependents
                </label>
                <p className="text-xs text-gray-500">
                  Recalculate the dates of events that follow on from this one and surface them as suggestions for
                  review. No events will be directly updated other than the current event.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            {isEdit && !form.isDirty ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex cursor-not-allowed">
                    <Button type="submit" variant="primary" disabled>
                      Save changes
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Make a change to save.</TooltipContent>
              </Tooltip>
            ) : (
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting && <Icon className="lucide--loader-2 animate-spin" />}
                {isEdit ? 'Save changes' : 'Create event'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>

      <ConfirmationModal
        isOpen={isUpdateConfirmOpen}
        onOpenChange={setIsUpdateConfirmOpen}
        title="Update this event?"
        onConfirm={handleConfirmUpdate}
        isLoading={updateMutation.isLoading}
      >
        {hasDependents && (
          <>
            <p className="text-sm text-gray-700">
              {dependentCount === 1
                ? 'Another event is calculated from this one.'
                : `${dependentCount} events are calculated from this one.`}
            </p>
            <p className="mt-3 text-sm text-gray-600">
              {recalculateDependents
                ? 'Their dates will be recalculated and proposed as suggestions for you to review. No dependent events are changed directly.'
                : "Their dates won't be recalculated, so they may no longer line up with this change."}
            </p>
          </>
        )}
      </ConfirmationModal>
    </Dialog>
  );
}
